import { useQuery } from '@tanstack/react-query';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';
import useGetQueryFn from '@/client/api/use-get-query-fn';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import { MARKETING_BASE } from '@/client/api/marketing/keys';

type DemandItem = {
  term: string;
  checked: boolean;
  direction: 'rising' | 'flat' | 'falling' | 'unknown';
  change_pct?: number;
  series?: (number | null)[];
  reason?: string;
};

type SearchDemand = { geo: string; time_range: string; items: DemandItem[] };

const DIRECTION_CLASS: Record<DemandItem['direction'], string> = {
  rising: 'text-emerald-700',
  flat: 'text-gray-500',
  falling: 'text-red-600',
  unknown: 'text-gray-400',
};

function useSearchDemandQuery() {
  const queryFn = useGetQueryFn<AdminEnvelope<SearchDemand>>({
    defaultErrorMessage: 'Could not load search demand',
    endpoint: `${MARKETING_BASE}/radar/performance/search-demand`,
  });
  return useQuery({
    queryKey: ['marketing', 'radar', 'performance', 'search-demand'],
    queryFn: () => queryFn(),
    // Weekly points, cached 12h server-side; each miss bills DataForSEO.
    staleTime: 1000 * 60 * 60,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
}

/**
 * Google Trends for the watched topics, Ghana, past 12 months. Social volume
 * says people are posting about a topic; this says they are searching for it.
 * A topic rising on both is the one worth a brief.
 */
export function SearchDemandPanel() {
  const { data, isLoading, error } = useSearchDemandQuery();
  const items = data?.items ?? [];

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="text-sm font-medium text-gray-900">Search demand in Ghana</div>
        <div className="text-xs text-gray-500">
          Google Trends, past 12 months, for watched topics. Last quarter vs the one before; the
          index is relative interest (0–100), not search counts.
        </div>
      </div>
      {error && <p className="px-4 py-3 text-sm text-red-600">Could not load search demand.</p>}
      {isLoading && <p className="px-4 py-3 text-sm text-gray-400">Loading…</p>}
      {!isLoading && !error && items.length === 0 && (
        <p className="px-4 py-3 text-sm text-gray-400">Watch a topic above to see its search demand.</p>
      )}
      <ul className="divide-y divide-gray-100">
        {items.map((it) => (
          <li key={it.term} className="flex items-center gap-4 px-4 py-2">
            <span className="w-48 truncate text-sm text-gray-900" title={it.term}>
              {it.term}
            </span>
            <div className="h-8 flex-1">
              {(it.series?.length ?? 0) > 1 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={it.series!.map((v, i) => ({ i, v }))}>
                    <Line type="monotone" dataKey="v" dot={false} strokeWidth={1.5} stroke="#2563eb" isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            <span className={cn('w-28 text-right text-sm tabular-nums', DIRECTION_CLASS[it.direction])}>
              {it.direction === 'unknown'
                ? it.checked
                  ? 'too little data'
                  : 'unavailable'
                : `${it.direction} ${it.change_pct != null ? `${it.change_pct > 0 ? '+' : ''}${it.change_pct}%` : ''}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
